import React, { useCallback, useEffect, useState } from 'react';
import { Box, Flex, SimpleGrid, Text, VStack, useToast } from '@chakra-ui/react';
import { TablePagination } from '../../../components/ui';
import {
  extractEquipmentKyc,
  getEquipmentKyc,
  listEquipmentKyc,
} from '../../../repositories/equipmentKyc.repository';
import { formatDateTimeDisplay } from '../../../utils/personName';
import {
  KycActionButtons,
  KycDocumentSection,
  KycExtractedSection,
  KycListFilters,
  parseKycSort,
  useKycCrud,
} from '../equipments/public-kyc/kycUi';
import { HubError, HubLoading, HubTable, hub } from './hubUi';

export function HubEquipmentKycList({ push }) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [submittedFrom, setSubmittedFrom] = useState('');
  const [submittedTo, setSubmittedTo] = useState('');
  const [sortValue, setSortValue] = useState('submittedAt:DESC');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const pageSize = 20;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { sortBy, sortDir } = parseKycSort(sortValue);
      const result = await listEquipmentKyc({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        sortBy,
        sortDir,
        submittedFrom: submittedFrom || undefined,
        submittedTo: submittedTo || undefined,
      });
      setRows(result.items || []);
      setTotal(result.total || 0);
    } catch (err) {
      setError(err?.message || 'Failed to load');
      toast({ title: err?.message || 'Failed to load', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sortValue, submittedFrom, submittedTo, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const crud = useKycCrud({ onSaved: () => load(), onDeleted: () => load() });

  return (
    <>
      <KycListFilters
        search={search}
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        submittedFrom={submittedFrom}
        submittedTo={submittedTo}
        onFrom={(e) => {
          setSubmittedFrom(e.target.value);
          setPage(1);
        }}
        onTo={(e) => {
          setSubmittedTo(e.target.value);
          setPage(1);
        }}
        sortValue={sortValue}
        onSort={(value) => {
          setSortValue(value);
          setPage(1);
        }}
        total={total}
        onClear={() => {
          setSearch('');
          setSubmittedFrom('');
          setSubmittedTo('');
          setSortValue('submittedAt:DESC');
          setPage(1);
        }}
      />
      {loading ? (
        <HubLoading />
      ) : error ? (
        <HubError message={error} onRetry={load} />
      ) : (
        <HubTable
          loading={false}
          rows={rows.map((row, index) => ({
            ...row,
            serial: (page - 1) * pageSize + index + 1,
          }))}
          onRowClick={(r) =>
            push({
              id: 'equipmentKycDetail',
              label: r.email,
              itemId: r.id,
            })
          }
          columns={[
            { key: 'serial', label: '#' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone' },
            { key: 'rcCount', label: 'RC' },
            { key: 'aadhaarCount', label: 'Aadhaar' },
            {
              key: 'submittedAt',
              label: 'Submitted',
              render: (r) => formatDateTimeDisplay(r.submittedAt),
            },
            crud.canEdit || crud.canDelete
              ? {
                  key: 'actions',
                  label: '',
                  render: (r) => (
                    <KycActionButtons
                      canEdit={crud.canEdit}
                      canDelete={crud.canDelete}
                      onEdit={() => crud.setEditRow(r)}
                      onDelete={() => crud.setDeleteRow(r)}
                    />
                  ),
                }
              : null,
          ].filter(Boolean)}
        />
      )}
      <TablePagination
        page={page}
        pageSize={pageSize}
        totalCount={total}
        totalPages={Math.max(1, Math.ceil((total || 0) / pageSize))}
        onPageChange={setPage}
      />
      {crud.dialogs}
    </>
  );
}

export function HubEquipmentKycDetail({ itemId, onDeleted }) {
  const toast = useToast();
  const [row, setRow] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      setRow(await getEquipmentKyc(itemId));
    } catch (err) {
      if (!silent) setError(err?.message || 'Failed to load');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (row?.extractedData?.status !== 'pending') return undefined;
    const timer = setInterval(() => {
      load(true);
    }, 4000);
    return () => clearInterval(timer);
  }, [load, row?.extractedData?.status]);

  const crud = useKycCrud({
    onSaved: (saved) => setRow(saved),
    onDeleted: () => onDeleted?.(),
  });

  if (loading) return <HubLoading />;
  if (error || !row) return <HubError message={error || 'Not found'} onRetry={() => load()} />;

  const fields = [
    { label: 'Email', value: row.email },
    { label: 'Phone', value: row.phone },
    { label: 'Submitted', value: formatDateTimeDisplay(row.submittedAt) },
  ];

  return (
    <VStack align="stretch" spacing={4}>
      <Flex justify="flex-end">
        <KycActionButtons
          canEdit={crud.canEdit}
          canDelete={crud.canDelete}
          onEdit={() => crud.setEditRow(row)}
          onDelete={() => crud.setDeleteRow(row)}
        />
      </Flex>
      <Box
        border="1px solid"
        borderColor={hub.cardBorder}
        borderRadius="16px"
        bg={hub.cardBg}
        p={4}
      >
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={4}>
          {fields.map((field) => (
            <Box key={field.label}>
              <Text fontSize="12px" color={hub.muted} fontWeight="600">
                {field.label}
              </Text>
              <Text fontSize="15px" fontWeight="700">
                {field.value || '—'}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
      </Box>
      <Box
        border="1px solid"
        borderColor={hub.cardBorder}
        borderRadius="16px"
        bg={hub.cardBg}
        p={4}
      >
        <KycDocumentSection rcFiles={row.rcFiles} aadhaarFiles={row.aadhaarFiles} />
      </Box>
      <Box
        border="1px solid"
        borderColor={hub.cardBorder}
        borderRadius="16px"
        bg={hub.cardBg}
        p={4}
      >
        <KycExtractedSection
          extractedData={row.extractedData}
          extracting={extracting}
          onExtract={async () => {
            if (extracting) return;
            setExtracting(true);
            try {
              const data = await extractEquipmentKyc(itemId);
              setRow(data);
            } catch (err) {
              toast({ title: err?.message || 'AI extraction failed', status: 'error' });
            } finally {
              setExtracting(false);
            }
          }}
        />
      </Box>
      {crud.dialogs}
    </VStack>
  );
}
